import { Button, makeStyles, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import Step1 from 'components/Forms/ExpeditionCreateForm/Step1';
import Step2 from 'components/Forms/ExpeditionCreateForm/Step2';
import MultiStepForm from 'components/Forms/MultiStepForm';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { postExpedition } from 'services/apiOperations';
import { DialogWidth, ExpeditionForm } from 'types';
import { formatExpeditionBeforePost } from 'utils/normalizer';
import { goBack } from 'utils/history';
import Dialog from 'components/Dialog';
import { colors } from 'theme';

type SubmissionError = {
  status: number;
  response: {
    body: {
      violations?: {
        code: string;
        message: string;
        propertyPath: string;
      }[];
    };
  };
};

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  title: {
    margin: '0',
  },
  buttonBar: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      margin: '0 10px',
    },
  },
});

const DUPLICATE_EXPEDITION_VIOLATION = 'DUPLICATE_EXPEDITION_VIOLATION';

const ExpeditionCreate: React.FC = () => {
  const context = useContext(AppContext);
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);

  const onSubmit: SubmitHandler<ExpeditionForm> = async (expedition: ExpeditionForm) => {
    if (isSubmitting) {
      return;
    }

    const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;

    if (null === sortingCenterId) {
      notify({
        message: t('expeditionCreate.creation.error'),
        severity: 'error',
      });
      return;
    }

    const formattedExpedition = formatExpeditionBeforePost(expedition, sortingCenterId);
    setIsSubmitting(true);
    setIsFormSubmitted(true);

    return postExpedition(formattedExpedition)
      .then(() => {
        notify({
          message: t('expeditionCreate.creation.success'),
          severity: 'success',
        });
        goBack(Routes.Expeditions, history);
        setIsSubmitting(false);
      })
      .catch((e: SubmissionError) => {
        if (e.status === 412) {
          if (e.response.body.violations) {
            const duplicateViolation = e.response.body.violations.find(
              (violation: { message: string }) => violation.message === DUPLICATE_EXPEDITION_VIOLATION,
            );
            if (duplicateViolation) {
              setDuplicateModalOpen(true);
              setIsSubmitting(false);
              return;
            }
          }
        }
      });
  };

  useEffect(() => {
    return () => {
      if (!isFormSubmitted) {
        notify(null);
      }
    };
  }, [isFormSubmitted]);

  const steps = [
    {
      label: t('expeditionCreateForm.create.step1'),
      component: Step1,
      nextButtonLabel: t('expeditionCreateForm.create.nextStep'),
      requiredFieldsLegend: true,
    },
    { label: t('expeditionCreateForm.create.step2'), component: Step2 },
  ];
  const initialData = {
    expeditionDate: null,
    expeditionTime: null,
    material: '',
    buyer: '',
    truckIdentifier: '',
    weight: undefined,
    conditioningQuantity: null,
    expeditionClientPortions: [],
  };

  return (
    <>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isDuplicateModalOpen}
        title={t('expeditions.create.duplicateModal.title')}
        width={DialogWidth.DEFAULT}
        renderContent={() => (
          <div className={classes.buttonBar}>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              onClick={() => {
                goBack(Routes.Expeditions, history);
              }}
            >
              {t('expeditions.create.duplicateModal.backToList')}
            </Button>
            <Button
              endIcon={<EditIcon />}
              variant="outlined"
              onClick={() => {
                setDuplicateModalOpen(false);
              }}
              style={{
                background: colors.grey900,
                color: 'white',
              }}
            >
              {t('expeditions.create.duplicateModal.edit')}
            </Button>
          </div>
        )}
      />
      <div className={classes.headerTitle}>
        <Typography variant="h1" className={classes.title}>
          {t('expeditions.create.title')}
        </Typography>
      </div>
      <MultiStepForm
        formName="expeditionCreateForm"
        backRoute="/expeditions"
        backIcon={<ArrowBackIcon />}
        onSubmit={onSubmit}
        steps={steps}
        initialData={initialData}
        setDuplicateModalOpen={setDuplicateModalOpen}
      />
    </>
  );
};
export default ExpeditionCreate;
